import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { MapPin, Car } from "lucide-react";
import api from "../lib/api";

const pinIcon = L.divIcon({
  className: "",
  html:
    '<div style="width:26px;height:26px;background:#4F46E5;border:2px solid #0F172A;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:2px 2px 0 0 #0F172A;"></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

export default function FleetMap() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(ref.current).setView([46.6, 2.4], 5);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap",
        maxZoom: 19,
      }).addTo(mapRef.current);
    }
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
    }
    const pts = vehicles.filter(
      (v) => v.geoloc_equipe && v.latitude != null && v.longitude != null && !isNaN(v.latitude) && !isNaN(v.longitude)
    );
    if (pts.length) {
      const group = L.featureGroup();
      pts.forEach((v) => {
        const m = L.marker([Number(v.latitude), Number(v.longitude)], { icon: pinIcon });
        m.bindPopup(
          `<b>${v.immatriculation}</b><br/>${v.conducteur || "Sans conducteur"}<br/>${(v.kilometrage || 0).toLocaleString("fr-FR")} km`
        );
        m.on("click", () => setTimeout(() => navigate(`/vehicules/${v.id}`), 600));
        group.addLayer(m);
      });
      group.addTo(mapRef.current);
      layerRef.current = group;
      mapRef.current.fitBounds(group.getBounds().pad(0.3), { maxZoom: 13 });
    }
    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 200);
  }, [vehicles, navigate]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const equipped = vehicles.filter((v) => v.geoloc_equipe && v.latitude != null && v.longitude != null);

  return (
    <div className="ml-64 p-8 min-h-screen">
      <div className="mb-8 mc-slide-up">
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Carte de la flotte</h1>
        <p className="text-slate-500 font-medium mt-1">
          {equipped.length} véhicule(s) équipé(s) GPS localisé(s) sur la carte
        </p>
      </div>

      <div className="nb-card p-4 mc-pop">
        {equipped.length === 0 ? (
          <div className="py-16 text-center" data-testid="fleetmap-empty">
            <MapPin size={40} strokeWidth={2.5} className="mx-auto text-slate-300 mb-3" />
            <p className="font-heading font-black text-xl">Aucun véhicule géolocalisé</p>
            <p className="text-slate-500 font-medium mt-1">
              Renseignez latitude/longitude sur un véhicule équipé GPS pour le voir ici.
            </p>
          </div>
        ) : (
          <div
            ref={ref}
            data-testid="fleet-map"
            style={{ height: "70vh", zIndex: 0 }}
            className="rounded-xl border-2 border-slate-900 overflow-hidden"
          />
        )}
      </div>

      {equipped.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {equipped.map((v) => (
            <button
              key={v.id}
              data-testid={`fleetmap-vehicle-${v.id}`}
              onClick={() => navigate(`/vehicules/${v.id}`)}
              className="nb-card p-4 text-left transition-all hover:-translate-y-1"
            >
              <div className="flex items-center gap-2 mb-1">
                <Car size={18} strokeWidth={2.5} className="text-[#4F46E5]" />
                <span className="font-mono-plate font-bold text-sm">{v.immatriculation}</span>
              </div>
              <p className="text-sm font-semibold">{v.conducteur || "Sans conducteur"}</p>
              <p className="text-xs text-slate-500 font-medium">{v.geoloc_position || `${v.latitude}, ${v.longitude}`}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
